<template>
	<v-container>
		<v-row class="contentRow">

			<!-- HEADER -->
			<v-col cols="12">
				<h1 class="mb-2">Edit details for</h1>
				<p class="mb-10 text-center">{{ username_ }}</p>
			</v-col>

			<!-- PASSWORD SECTION -->
			<v-row style="width:100%;" v-if="isSelf || isAdmin">
				<v-col cols="12">
					<h3 class="text-center">
						Password
					</h3>
				</v-col>
			</v-row>

			<!-- ADMIN GENERATE PASSWORD -->
			<v-row style="width: 100%;" v-if="!isSelf && isAdmin">
				<v-col cols="6" class="text-center">
					<p class="mb-2 text-center">
						Generate password
					</p>
				</v-col>

				<v-col cols="6">
					<v-btn
						color="primary"
						@click="generatePassword()"
					>
						Generate new password
					</v-btn>
				</v-col>
			</v-row>

			<!-- SHOW PASSWORD BUTTON -->
			<v-row style="width:100%;">
				<v-col align="center">
					<v-btn
						v-if="isSelf && !displayPasswordField"
						class="text-center"
						color="primary"
						@click="changePasswordField"
					>
						Change Password
					</v-btn>
				</v-col>
			</v-row>

			<!-- PASSWORD FORM -->
			<v-row
				v-if="displayPasswordField"
				style="width:100%;"
			>

				<!-- OLD PASSWORD -->
				<v-row
					style="width:100%;"
					v-if="isSelf"
				>
					<v-col cols="6" class="text-center">
						<p class="mb-2 text-center">
							Old password
						</p>
					</v-col>

					<v-col cols="6">

						<v-text-field
							v-model="old_password"
							:type="showOldPassword ? 'text' : 'password'"
							outlined
							dense
							autocomplete="current-password"
							@paste.native.prevent="preventClipboard"
							@copy.native.prevent="preventClipboard"
							@cut.native.prevent="preventClipboard"
						>
							<template v-slot:append>

								<v-icon
									small
									class="mr-2"
									@click="showOldPassword = !showOldPassword"
								>
									{{
										showOldPassword
											? 'fas fa-eye-slash'
											: 'fas fa-eye'
									}}
								</v-icon>

							</template>
						</v-text-field>

					</v-col>
				</v-row>

				<!-- NEW PASSWORD -->
				<v-row
					style="width:100%;"
					v-if="isSelf"
				>
					<v-col cols="6" class="text-center">
						<p class="mb-2 text-center">
							New password
						</p>
					</v-col>

					<v-col cols="6">

						<v-text-field
							v-model="new_password"
							:type="showNewPassword ? 'text' : 'password'"
							outlined
							dense
							autocomplete="new-password"
							:success="
								new_password &&
								isPasswordValid(new_password)
							"
							:error="
								new_password &&
								!isPasswordValid(new_password)
							"
							@paste.native.prevent="preventClipboard"
							@copy.native.prevent="preventClipboard"
							@cut.native.prevent="preventClipboard"
						>
							<template v-slot:append>

								<v-icon
									small
									class="mr-2"
									@click="showNewPassword = !showNewPassword"
								>
									{{
										showNewPassword
											? 'fas fa-eye-slash'
											: 'fas fa-eye'
									}}
								</v-icon>

							</template>
						</v-text-field>

						<!-- PASSWORD GUIDANCE -->
						<PasswordGuidance
							:password="new_password"
							:rejection-errors="passwordErrors"
						/>

					</v-col>
				</v-row>

				<!-- CONFIRM PASSWORD -->
				<v-row
					style="width:100%;"
					v-if="isSelf"
				>
					<v-col cols="6" class="text-center">
						<p class="mb-2 text-center">
							Confirm password
						</p>
					</v-col>

					<v-col cols="6">

						<v-text-field
							v-model="confirm_password"
							:type="
								showConfirmPassword
									? 'text'
									: 'password'
							"
							outlined
							dense
							autocomplete="new-password"
							:error="
								confirm_password &&
								new_password !== confirm_password
							"
							:error-messages="
								confirm_password &&
								new_password !== confirm_password
									? ['Passwords do not match']
									: []
							"
							@paste.native.prevent="preventClipboard"
							@copy.native.prevent="preventClipboard"
							@cut.native.prevent="preventClipboard"
						>
							<template v-slot:append>

								<v-icon
									small
									class="mr-2"
									@click="
										showConfirmPassword =
											!showConfirmPassword
									"
								>
									{{
										showConfirmPassword
											? 'fas fa-eye-slash'
											: 'fas fa-eye'
									}}
								</v-icon>

							</template>
						</v-text-field>

					</v-col>
				</v-row>

				<!-- CHANGE PASSWORD BUTTON -->
				<v-row style="width:100%;">
					<v-col align="end">

						<v-btn
							v-if="isSelf"
							class="text-center"
							color="primary"
							:disabled="!canSubmitPassword"
							@click="changePassword()"
						>
							Change Password
						</v-btn>

					</v-col>
				</v-row>

			</v-row>

			<!-- PERSONAL INFO -->
			<v-row style="width:100%;">
				<v-col cols="12">
					<h3 class="text-center">
						Personal information
					</h3>
				</v-col>
			</v-row>

			<!-- FIRST NAME -->
			<v-row
				style="width:100%;"
				v-if="isSelf || isAdmin"
			>
				<v-col cols="6" class="text-center">
					<p class="mb-2 text-center">
						Change first name
					</p>
				</v-col>

				<v-col cols="6">
					<v-text-field
						append-icon="far fa-edit"
						outlined
						v-model="new_firstname"
						label="Firstname"
					/>
				</v-col>
			</v-row>

			<!-- LAST NAME -->
			<v-row
				style="width:100%;"
				v-if="isSelf || isAdmin"
			>
				<v-col cols="6" class="text-center">
					<p class="mb-2 text-center">
						Change last name
					</p>
				</v-col>

				<v-col cols="6">
					<v-text-field
						append-icon="far fa-edit"
						outlined
						v-model="new_lastname"
						label="Lastname"
					/>
				</v-col>
			</v-row>

			<!-- EMAIL -->
			<v-row
				style="width:100%;"
				v-if="isSelf || isAdmin"
			>
				<v-col cols="6" class="text-center">
					<p class="mb-2 text-center">
						Change E-mail
					</p>
				</v-col>

				<v-col cols="6">
					<v-text-field
						append-icon="far fa-edit"
						outlined
						v-model="new_email"
						label="E-mail"
					/>
				</v-col>
			</v-row>

			<!-- EMAIL NOTIFICATIONS -->
			<v-row style="width: 100%;" v-if="!isSelf && !isAdmin">
				<v-col cols="6" class="text-center">
					<p class="mb-2 text-center">Firstname</p>	
				</v-col>
				<v-col cols="6" class=" text-center"> 
					<p class="mb-2 text-center">{{ first_name }}</p>
				</v-col>
			</v-row>
			<v-row style="width: 100%;" v-if="!isSelf && !isAdmin">
				<v-col cols="6" class="text-center">
					<p class="mb-2 text-center">Lastname</p>	
				</v-col>
				<v-col cols="6" class=" text-center"> 
					<p class="mb-2 text-center">{{ last_name }}</p>
				</v-col>
			</v-row>
			<v-row style="width: 100%;" v-if="!isSelf && !isAdmin">
				<v-col cols="6" class="text-center">
					<p class="mb-2 text-center">Email</p>	
				</v-col>
				<v-col cols="6" class=" text-center"> 
					<p class="mb-2 text-center">{{ email }}</p>
				</v-col>
			</v-row>
			<v-row
				style="width:100%;"
				v-if="isSelf || isAdmin"
			>
				<v-col cols="2">
				</v-col>

				<v-col cols="10">
					<v-checkbox
						v-model="email_notifications"
						:label="`Enable email notifications`"
					></v-checkbox>
				</v-col>
			</v-row>

			<!-- SAVE -->
			<v-row
				style="width:100%;"
				v-if="isSelf || isAdmin"
			>
				<v-col align="end" cols="12">

					<v-btn
						color="primary"
						@click="update()"
					>
						Save changes
					</v-btn>
                </v-col>
			</v-row>

			<v-row class="contentRow" v-if="hasAccess">
				<v-col cols="2"></v-col>
				<v-col cols="6" class="text-left">
					<h4>List of Current User Roles</h4>
					<v-data-table
					light
					:headers="headers"
					:items="roles"
					:items-per-page="roles.length"
					:footer-props="{
					}"
					hide-default-footer
					>
					</v-data-table>
				</v-col>
				<v-col cols="4"></v-col>
						
			</v-row>
			<v-row style="width:100%;" v-if="hasAccess">
				<v-col cols="12">
					<h3 class="text-center">
						Edit roles
					</h3>
				</v-col>
			</v-row>

			<v-row style="width:100%;" v-if="hasAccess">
				<v-col cols="6">
					<p class="text-center">Add a role</p>
				</v-col>
				<v-col cols="6">
					<v-select
						label="Select Role"
						outlined
						v-model="selectedRole"
						:items="allRoles"
						item-value="id"
						item-text="name"
					>
					</v-select>
				</v-col>
			</v-row>

			<v-row style="width: 100%;" v-if="hasAccess">
				<v-col align="end" cols="12">
					<v-btn
						color="primary"
						@click="addRole()"
						>
					Add role</v-btn>
				</v-col>
			</v-row>

			<v-row style="width:100%;" v-if="hasAccess">
				<v-col cols="6">
					<p class="text-center">Remove role</p>
				</v-col>
				<v-col cols="6">
					<v-select
						label="Select Role"
						outlined
						v-model="selectedRemoveRole"
						:items="roles"
						item-value="id"
						item-text="name"
					>
					</v-select>
				</v-col>
			</v-row>
            <v-row style="width: 100%;" v-if="hasAccess">
				<v-col align="end" cols="12">
					<v-btn
						color="red"
						@click="removeRole()"
						>
					Remove role</v-btn>
				</v-col>
			</v-row>
		</v-row>

		<!-- GENERATED PASSWORD DIALOG -->
		<v-dialog
			v-model="displayPassword"
			max-width="500px"
		>
			<v-card class="d-block">

				<v-card-title
					class="pt-7 secondary--text text-center d-block"
					style="font-size:15pt;"
				>
					New password
				</v-card-title>

				<v-card-subtitle>
					{{ generatedPassword }}
				</v-card-subtitle>

				<v-card-text>
					This password will only be shown once,
					make sure you note it down.
				</v-card-text>

				<v-card-actions>

					<v-btn
						color="primary"
						text
						@click="displayPassword = false"
					>
						Close
					</v-btn>

				</v-card-actions>

			</v-card>
		</v-dialog>

	</v-container>
</template>

<script>
import UsersAPI from "../api/connections/users"
import RolesAPI from "../api/roles"
import LoadingState from "../consts/LoadingState"
import { mapGetters } from "vuex"

import PasswordGuidance from "../components/PasswordGuidance.vue"

import {
	getPasswordValidationErrors,
	isPasswordValid,
} from "../utils/passwordPolicy"

export default {
	name: "Edit",

	components: {
		PasswordGuidance,
	},

	data() {
		return {
			isAdmin: null,
			email: null,
			new_email: null,
			hasAccess: null,

			new_firstname: null,
			new_lastname: null,

			selectedRole: null,
			selectedRemoveRole: null,

			first_name: null,
			last_name: null,

			username_: null,
			isSelf: null,

			email_notifications: false,

			old_password: null,
			new_password: null,
			confirm_password: null,

			passwordErrors: [],

			showOldPassword: false,
			showNewPassword: false,
			showConfirmPassword: false,

			generatedPassword: null,
			displayPassword: false,
			displayPasswordField: false,

			roles: [],
			allRoles: [],

			headers: [
				{
					align: "left",
					text: "Roles",
					value: "name",
				},
			],
		}
	},

	mounted() {
		this.hasAccess = this.$accessToOperation("USR", "U")

		this.username_ = this.$route.params.username

		this.isSelf =
			(this.username == this.username_)
				? true
				: false

		this.isAdmin =
			(this.user.permissions.USR.U == 0)
				? false
				: true

		this.fetchAPI()
	},

	computed: {
		...mapGetters("user", [
			"isLoggedIn",
			"getUser",
		]),

		username() {
			return this.getUser.username
		},

		user() {
			return this.getUser
		},

		canSubmitPassword() {
			return (
				this.old_password &&
				this.new_password &&
				this.confirm_password &&
				this.new_password === this.confirm_password &&
				isPasswordValid(this.new_password)
			)
		},
	},

	methods: {
		isPasswordValid,

		preventClipboard(e) {
			e.preventDefault()

			this.$store.dispatch(
				"loading/" +
				LoadingState.ACTION_SET_ERROR,
				"Copy, cut, and paste are disabled for password fields.",
				{ root: true }
			)
		},

		changePasswordField(){
			this.displayPasswordField = true
		},

		generatePassword: function() {
			UsersAPI.changePassword(this.username_).then(
				(rawdata) => {

					this.$store.dispatch(
						"loading/" +
						LoadingState.ACTION_LOADING_SUCCESS,
						"New password set",
						{ root: true }
					)

					this.generatedPassword =
						rawdata[0].generatedPassword

					this.displayPassword = true
				})
				.catch((reason) => {
					this.$store.dispatch(
						"loading/" +
						LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					)
				})
		},

		changePassword() {

			this.passwordErrors =
				getPasswordValidationErrors(
					this.new_password
				)

			if (this.passwordErrors.length > 0) {
				return
			}

			if (
				this.new_password !==
				this.confirm_password
			) {
				this.passwordErrors = [
					"Passwords do not match.",
				]
				return
			}

			UsersAPI.changePassword(
				this.username_,
				this.old_password,
				this.new_password,
				this.confirm_password
			)
				.then(() => {

					this.$store.dispatch(
						"loading/" +
						LoadingState.ACTION_LOADING_SUCCESS,
						"Successfully changed password!",
						{ root: true }
					)

					this.old_password = null
					this.new_password = null
					this.confirm_password = null
					this.passwordErrors = []
				})
				.catch(reason => {

					this.$store.dispatch(
						"loading/" +
						LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					)
				})
		},

		fetchAPI() {

			this.$store.dispatch(
				"loading/" +
				LoadingState.ACTION_START_LOADING,
				"Retrieving user data...",
				{ root: true }
			)

			UsersAPI.getUser(this.username_)
				.then((rawdata) => {

					this.first_name =
						rawdata[0].user.firstname

					this.last_name =
						rawdata[0].user.lastname

					this.email =
						rawdata[0].user.email

					this.new_firstname =
						this.first_name

					this.new_lastname =
						this.last_name

					this.new_email =
						this.email

					this.email_notifications =
						rawdata[0].user
							.enableEmailNotifications === 0
							? false
							: true
				})
				.catch(reason => {

					this.$store.dispatch(
						"loading/" +
						LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					)
				})

			if (this.isAdmin) {

				UsersAPI.getRoles(this.username_).then(
					(rawdata) => {
						this.roles = rawdata.items
					})
					.catch(reason => {

						this.$store.dispatch(
							"loading/" +
							LoadingState.ACTION_SET_ERROR,
							reason,
							{ root: true }
						)
					})

				RolesAPI.getRoles()
					.then((rawdata) => {
						this.allRoles = rawdata
					})
					.catch(reason => {

						this.$store.dispatch(
							"loading/" +
							LoadingState.ACTION_SET_ERROR,
							reason,
							{ root: true }
						)
					})
			}

			this.$store.dispatch(
				"loading/" +
				LoadingState.ACTION_END_LOADING,
				null,
				{ root: true }
			)
		},

		update:function() {

			UsersAPI.updateUser(
				this.username_,
				this.new_email,
				this.new_firstname,
				this.new_lastname,
				this.email_notifications
			)
				.then(() => {

					this.$store.dispatch(
						"loading/" +
						LoadingState.ACTION_LOADING_SUCCESS,
						"Successfully saved changes!",
						{ root: true }
					)
					this.new_firstname = null
					this.new_lastname = null
					this.new_email = null

					this.fetchAPI()
				})
				.catch(reason => {

					this.$store.dispatch(
						"loading/" +
						LoadingState.ACTION_SET_ERROR,
						reason,
						{ root: true }
					)
				})
		},
		addRole: function() {
			UsersAPI.addRoles(this.username_, [this.selectedRole]).then(
				() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						"Successfully added role!",
						{ root: true } 
					)	
					this.selectedRole = null
					this.fetchAPI()
				}
			).catch(reason => {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					reason,
					{ root: true}
				)
			})
		},
		removeRole: function() {
			UsersAPI.removeRole(this.username_, this.selectedRemoveRole).then(
				() => {
					this.$store.dispatch(
						"loading/" + LoadingState.ACTION_LOADING_SUCCESS,
						"Successfully removed role!",
						{ root: true } 
					)	
					this.selectedRemoveRole = null
					this.fetchAPI()
				}
			).catch(reason => {
				this.$store.dispatch(
					"loading/" + LoadingState.ACTION_SET_ERROR,
					reason, 
					{ root: true }
				)
			})
		}
	}
}
</script>